import { Place } from '../../lib/interfaces';
import { isIdea, TSDate } from '../../lib/time';

// ---------------------------------------------------------------------------
// Semantic model
// ---------------------------------------------------------------------------
// Hotel H occupies: noon of H.startdatetime  →  noon of H.enddatetime
//
//   Evening of H.startdatetime              (check-in night)
//   Morning + Evening of every day strictly between startdatetime and enddatetime
//   Morning of H.enddatetime               (checkout morning)

export type DaySlot = { morning: Place | null; evening: Place | null };

export function computeDaySlots(lodgings: Place[]): Map<number, DaySlot> {
    const slots = new Map<number, DaySlot>();

    const ensure = (day: TSDate): DaySlot => {
        const k = day.daysSinceEpoch;
        if (!slots.has(k)) slots.set(k, { morning: null, evening: null });
        return slots.get(k)!;
    };

    for (const lodging of lodgings) {
        // isIdea, not a presence check: the Firestore converter maps a missing startdatetime to the
        // 1900 sentinel (IDEA_TSDATE), so `!lodging.startdatetime` never fires for legacy records. A
        // sentinel check-in against a real check-out would walk ~46,000 days into this Map below.
        if (isIdea(lodging.startdatetime) || isIdea(lodging.enddatetime)) continue;
        const checkin  = lodging.startdatetime;
        const checkout = lodging.enddatetime;

        ensure(checkin).evening = lodging;

        let d = checkin.plus({ days: 1 });
        while (d.isBefore(checkout)) {
            const slot = ensure(d);
            slot.morning = lodging;
            slot.evening = lodging;
            d = d.plus({ days: 1 });
        }

        ensure(checkout).morning = lodging;
    }

    return slots;
}

export function getSlot(day: TSDate, slots: Map<number, DaySlot>): DaySlot {
    return slots.get(day.daysSinceEpoch) ?? { morning: null, evening: null };
}

// ---------------------------------------------------------------------------
// Lodging bars for grid layout
// ---------------------------------------------------------------------------
// The lodging row uses a 14-column grid (2 half-columns per day):
//   Day d (0-indexed): morning = column 2d+1, evening = column 2d+2  (1-indexed CSS Grid)
//
// Each bar is a contiguous run of half-columns covered by the same lodging (or none).
//
// Warning rules for null bars:
//   morning half: warn unless it is the morning of tripStart (arriving — no prior hotel expected)
//   evening half: warn unless it is the evening of tripEnd  (departing — no future hotel expected)

export type LodgingBar = {
    colStart: number;   // 1-indexed CSS Grid column, inclusive
    colEnd: number;     // exclusive
    lodging: Place | null;
    warn: boolean;
};

export function computeLodgingBars(
    week: TSDate[],
    slots: Map<number, DaySlot>,
    tripStart: TSDate,
    tripEnd: TSDate,
): LodgingBar[] {
    // Build 14 half-slots in order: [morning₀, evening₀, morning₁, evening₁, …]
    const halfSlots: Array<{ lodging: Place | null; shouldWarn: boolean }> = [];

    for (let d = 0; d < week.length; d++) {
        const day   = week[d];
        const slot  = getSlot(day, slots);
        const inRange = day.isEqualOrAfter(tripStart) && day.isEqualOrBefore(tripEnd);

        halfSlots.push({
            lodging:     slot.morning,
            shouldWarn:  slot.morning === null && inRange && !day.equals(tripStart),
        });
        halfSlots.push({
            lodging:     slot.evening,
            shouldWarn:  slot.evening === null && inRange && !day.equals(tripEnd),
        });
    }

    // Group consecutive same-lodging half-slots into bars
    const bars: LodgingBar[] = [];
    let i = 0;
    while (i < halfSlots.length) {
        const groupId = halfSlots[i].lodging?.docid ?? null;
        let j = i + 1;
        while (j < halfSlots.length && (halfSlots[j].lodging?.docid ?? null) === groupId) {
            // For null runs, break when warn status changes so warn/non-warn segments stay separate
            if (groupId === null && halfSlots[j].shouldWarn !== halfSlots[i].shouldWarn) break;
            j++;
        }
        const group = halfSlots.slice(i, j);
        bars.push({
            colStart: i + 1,
            colEnd:   j + 1,
            lodging:  halfSlots[i].lodging,
            warn:     group.some(s => s.shouldWarn),
        });
        i = j;
    }

    return bars;
}
